import {SnapGeo, StickMaterial} from "./StickMaterial";
import {cellHasDoor, FrameBlockExt, InteriorStick} from "./FrameBlockExt";
import {assertNotNaN, makeMullionResize, makeMullionThicknessAbsorb, stickThicknessVar} from "./Util";
import {FrameBlockItem} from "./FrameBlockItem";
import {FrameGridView} from "./FrameGridScene";
import {MdFrameBlock, MdFrameStick} from "../../model/frame/MdFrameBlock";
import {FrameBlockRow} from "./FrameBlockRow";
import {absorbSizeDelta} from "./BlockOps";
import {FrmVar} from "../../drawfrm/FrmVar";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";

export class FrameBlockColumn extends FrameBlockExt {

    // horizontal mullions between vertically-stacked cells. interior[i] sits
    // between children[i] and children[i+1]; undefined when none is declared.
    // These consume flow height (the column grows by their thickness).
    private interior: (InteriorStick | undefined)[] = [];

    constructor(scene: FrameGridView, model: MdFrameBlock, parent?: FrameBlockRow|FrameBlockColumn) {
        super(scene, model, parent);

        const spec = this.elev.frameSpec;

        let pendingGap: MdFrameStick | undefined;
        this.model.children.forEach(c => {
            if(c.kind === "stick") {
                if(this.children.length === 0) this.leadingEdgeStick = c.stick;
                else pendingGap = c.stick;
                return;
            }

            if(this.children.length > 0) {
                this.interior[this.children.length - 1] = pendingGap ? this.makeInterior(pendingGap, spec) : undefined;
            }
            pendingGap = undefined;

            if(c.kind === "item") {
                const item = new FrameBlockItem(this.scene, c.item, this);
                this.children.push(item);
                this.scene.parentThing.add(item);
            } else if(c.block.kind === "column") {
                this.children.push(new FrameBlockColumn(this.scene, c.block, this));
            } else if(c.block.kind === "row") {
                this.children.push(new FrameBlockRow(this.scene, c.block, this));
            }
        });

        if(pendingGap) this.trailingEdgeStick = pendingGap;
    }

    private makeInterior(stickModel: MdFrameStick, spec: MdFrameSpec): InteriorStick {
        const length = new FrmVar(`${stickModel.id}:len`, 0);
        const thickness = stickThicknessVar(stickModel, spec);

        // this mullion divides the cell just pushed (above) from the one about to be
        // pushed next (below). Capture their indices and resolve lazily, since the
        // lower cell doesn't exist yet. Dragging it reallocates height between them.
        const topIdx = this.children.length - 1;
        const bottomIdx = this.children.length;

        // each piece of the mullion (see splitAtVoids) drags and edits as the whole.
        const build = (length: FrmVar, id: string) => new StickMaterial(this.elev, {
            id,
            stickId: stickModel.id,
            model: stickModel,
            view: this.scene,
            orientation: "horizontal",
            length,
            thickness,
            resize: makeMullionResize({
                leading: () => this.children[topIdx].height!,
                trailing: () => this.children[bottomIdx].height!,
                relayout: () => this.relayout(),
                snapsUnder: side => this.isStep(side === "leading" ? topIdx : bottomIdx),
                snapUnder: side => this.snapStepToEdge(side === "leading" ? topIdx : bottomIdx),
                status: s => this.elev.dragStatus.emit(s),
                cellsUnderMin: () => this.cellsUnderMin(),
                // a resized neighbour block scales its contents with it.
                onResize: (side, from, to) => {
                    const cell = this.children[side === "leading" ? topIdx : bottomIdx];
                    if(cell instanceof FrameBlockExt) absorbSizeDelta(spec, cell.model, "height", to - from);
                },
            }),
            absorbThickness: makeMullionThicknessAbsorb({
                leading: () => this.absorbTarget(topIdx),
                trailing: () => this.absorbTarget(bottomIdx),
            }),
            centerSnap: () => this.centerSnapGeo(),
        });
        const add = (mat: StickMaterial) => {
            this.scene.parentThing.add(mat);
            this.scene.registerStick(mat);
            return mat;
        };

        const stick = add(build(length, stickModel.id));
        const parts: InteriorStick["parts"] = [];
        // a split-off piece selects on its own, so it gets its own id (see pieceStickId).
        const makePart = () => {
            const id = `${stickModel.id}#${parts.length}`;
            const partLength = new FrmVar(`${id}:len`, 0);
            return {mat: add(build(partLength, id)), length: partLength};
        };
        return {stick, length, thickness, parts, makePart};
    }

    // this column's own horizontal centre-line, offered as a snap target while its
    // mullion is dragged so the stick can be dropped exactly on centre. Only when
    // the column holds a single mullion — with more than one there's no one stick
    // the centre belongs to — and only once the column has been laid out.
    private centerSnapGeo(): SnapGeo | null {
        if(this.interior.filter(s => !!s).length !== 1) return null;

        const r = this.layoutRect;
        if(!r) return null;

        return {center: r.y + r.height / 2, alongMin: r.x, alongMax: r.x + r.width};
    }

    // the height var of the cell at `idx` when it can give height up to a growing
    // mullion, else null: a door-bearing cell's height is dictated by the door.
    private absorbTarget(idx: number): FrmVar | null {
        const cell = this.children[idx];
        if(!cell || cellHasDoor(cell)) return null;
        return cell.height ?? null;
    }

    // see FrameBlockExt.splitInteriorAtVoids.
    protected splitInteriorAtVoids() {
        this.interior.forEach((s, i) => {
            if(s) this.splitAtVoids(s, this.children[i], this.children[i + 1]);
        });
    }

    // total height consumed by the column's sticks: the interior mullions and its edge
    // sticks (their thicknesses are constants as far as the layout solver is concerned).
    interiorHeightTotal(): number {
        return this.edgeThicknessTotal() + this.interior.reduce((acc, s) => acc + (s ? s.thickness.get() : 0), 0);
    }

    getWidth(strict: boolean) {
        if(this.width) return this.width.get();

        for(let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            const value = child.getWidth(false)
            if(!isNaN(value)) return value;
        }

        if(strict) {
            throw new Error(`${this.model.id}: can't get width for column`)
        }

        return NaN;
    }

    getHeight(strict: boolean) {
        if(this.height) return this.height.get();

        return this.interiorHeightTotal() + this.children.reduce((acc, c) => {
            return acc + assertNotNaN(c.getHeight(strict), strict, `${this.model.id}: can't calculate height for column`)
        }, 0);
    }

    // ref is based on the top left
    reposition(refX: number, refY: number) {
        this.setLayoutRect(refX, refY);
        const colWidth = this.getWidth(true);

        // the leading edge stick (head) takes the first slot; the root draws it.
        refY += this.edgeThickness(this.leadingEdgeStick);

        this.children.forEach((c, i) => {
            c.reposition(refX, refY);
            refY += c.getHeight(true);

            // the door leaf draws down over the threshold under it (see MdFrameSpec's
            // door height).
            if(c instanceof FrameBlockItem && c.model.kind === "doors") {
                const next = this.children[i + 1];
                c.setDrawnBottomExtension(next?.model.kind === "threshold" ? next.getHeight(true) : 0);
            }

            if(i < this.children.length - 1) {
                const s = this.interior[i];
                if(s) {
                    // horizontal mullion fills the gap and spans the column width, short of
                    // a jamb both neighbours carry (see interiorTrim).
                    const cross = {min: refY, max: refY + s.thickness.get()};
                    const left = this.interiorTrim(c, this.children[i + 1], "left", cross);
                    const right = this.interiorTrim(c, this.children[i + 1], "right", cross);
                    s.stick.graphic.position.set(refX + left, refY);
                    s.length.set(colWidth - left - right, {debug: "stick-layout"});
                    refY += s.thickness.get();
                }
            }
        });
    }
}
